import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

import getUser from 'state/User/selectors';
import LoadingIcon from 'styles/icons/player/Controls/Play/LoadingIcon';
import usePlayerActions from 'components/Player/PlayerActions/usePlayerActions';
import usePlayerState from 'contexts/PlayerState/usePlayerState';
import useTheme from 'contexts/Theme/useTheme';

import { extractClipSlugAndId } from '../../utils/HighlightsUtils';
import { playerCoordinator } from './Coordinator';

import { useAdsPlayerState } from 'ads';
import type { HighlightsConfig } from './Coordinator';

// Styled container for centered loading spinner
const SpinnerContainer = styled.div<{ isDark: boolean }>(({ isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: isDark ? '#1d1f20' : '#f4f5f6',
}));

/**
 * Custom hook to extract and memoize user configuration for SDK initialization.
 * Only returns a new object when relevant user properties change.
 */
function useUserConfig(user: any) {
  return useMemo(() => {
    if (!user) {
      return null;
    }

    return {
      isAnonymous: user.session?.isAnonymous,
      profileId: user.session?.profileId,
      sessionId: user.session?.sessionId,
    };
  }, [
    user?.session?.isAnonymous,
    user?.session?.profileId,
    user?.session?.sessionId,
  ]);
}

/**
 * Props for the Clips component
 */
export interface HighlightsSDKProps {
  /** API key for authentication */
  apiKey?: string;
  /** CSS class name for the SDK container */
  className?: string;
  /** placement ID for the player */
  placementId?: string;
  /** style ID for theming */
  styleId?: string;
  /** Height of the player (px or string with units) */
  height?: string | number;
  /** Page context for analytics */
  pageContext?: string;
  /** Inline CSS styles for the container */
  style?: {
    [key: string]: string | number;
  };
  /** User authentication token */
  token?: string;
  /** Width of the player (px or string with units) */
  width?: string | number;
  /** Podcast ID for brand context */
  podcastId?: number;
  /** Podcast name for analytics */
  podcastName?: string;
  /** Station ID for brand context */
  stationId?: number;
  /** Station name for analytics */
  stationName?: string;
  /** Combined clip slug and ID for direct clip loading (format: "slug-shortId_uuid") */
  clipSlugAndId?: string;
  /**
   * Specifies which Highlights SDK implementation to use —
   * `'legacy'` for the old setup or `'polaris'` for the new Polaris-based version.
   */
  highlightsSDKSourceType: 'legacy' | 'polaris';
  /** Indicates whether ads are enabled to manage layout behavior accordingly. */
  isAdsEnabled?: boolean;
}

export function HighlightsSDK({
  placementId,
  apiKey,
  styleId,
  width = '100%',
  height = 440,
  className = 'gen-sdk-class',
  style,
  token,
  podcastId,
  podcastName,
  stationId,
  stationName,
  clipSlugAndId,
  highlightsSDKSourceType,
  isAdsEnabled,
}: HighlightsSDKProps) {
  const isInitializedRef = useRef(false);
  const [showExpandViewLoader, setShowExpandViewLoader] =
    useState(!!clipSlugAndId);
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const theme = useTheme();
  const playerState = usePlayerState();
  const playerActions = usePlayerActions();
  const adState = useAdsPlayerState();

  // Extract user config that only changes when relevant properties change
  const userConfig = useUserConfig(user);

  // Track previous player state to detect changes
  const prevPlayerStateRef = useRef(playerState?.playbackState);

  // Set up player coordination for audio/video synchronization and dispatch
  useEffect(() => {
    if (!playerState || !playerActions) {
      return;
    }

    // Set player actions in the coordinator so it can control audio playback
    playerCoordinator.setPlayerActions(playerActions as any);

    // Set player state for coordination
    playerCoordinator.setPlayerState(playerState, adState);

    // Set the Redux dispatch function for opening authentication modals
    playerCoordinator.setDispatch(dispatch);
  }, [
    playerState?.playbackState,
    playerActions,
    adState,
    playerState,
    adState?.adPlaybackState,
    adState?.adIsPlaying,
    dispatch,
  ]);

  // Monitor player state changes for debugging
  useEffect(() => {
    const currentPlaybackState = playerState?.playbackState;
    const prevPlaybackState = prevPlayerStateRef.current;

    if (currentPlaybackState !== prevPlaybackState) {
      prevPlayerStateRef.current = currentPlaybackState;
    }
  }, [playerState?.playbackState]);

  // Initialize Clips SDK with user context and brand information
  // Client-side only - useEffect only runs in browser, not on server
  useEffect(() => {
    // Double-check we're on client side
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Clean up previous initialization if it exists
      if (isInitializedRef.current) {
        playerCoordinator.cleanup();
        isInitializedRef.current = false;
      }

      // Add delay only if genuin-user-data is not present and user is anonymous
      // This gives time for the SDK to be loaded and user data to be available
      // const delay = hasClipsUserData ? 0 : 1000;

      // const initTimeout = setTimeout(() => {
      // Build brand context array with podcast/station IDs
      const brandContext: Array<{ id: string; type: 'podcast' | 'station' }> =
        [];
      if (podcastId) {
        brandContext.push({ id: podcastId.toString(), type: 'podcast' });
      }
      if (stationId) {
        brandContext.push({ id: stationId.toString(), type: 'station' });
      }

      // Initialize Clips SDK with user authentication and parameters
      const config: HighlightsConfig = {
        // Only pass token and user parameters if user is not anonymous
        ...(userConfig &&
          !userConfig?.isAnonymous && {
            // If no token provided, use user's profileId as token
            token:
              token ??
              (userConfig.profileId ?
                `${userConfig.profileId}@iheartmedia.com`
              : undefined),
            // Pass user parameters if user is logged in
            ...(userConfig.profileId && {
              params: {
                name: userConfig.profileId ?? undefined,
                profileId:
                  userConfig.profileId ?
                    Number(userConfig.profileId)
                  : undefined,
                brandUserIdentity: userConfig.profileId,
              },
            }),
          }),
      };

      // Extract and validate clip ID from URL parameter
      const { clipId, clipSlug } = extractClipSlugAndId(clipSlugAndId);

      // Configure starting video if clip ID or slug is provided
      if (clipId || clipSlug) {
        config.start_video_slug = clipId || clipSlug;
      }

      // Add brand context if available
      if (brandContext.length > 0) {
        config.brand_context = brandContext;
      }

      // Set up callback for feed loaded event to hide loader
      playerCoordinator.setOnFeedLoadedCallback(() => {
        setShowExpandViewLoader(false);
      });

      // Initialize the player coordinator with the configuration and context
      playerCoordinator.init(
        {
          config,
          stationId,
          stationName,
          podcastId,
          podcastName,
          sessionId: userConfig?.sessionId,
        },
        Boolean(podcastId), // Flag to indicate if this is a podcast context
      );

      isInitializedRef.current = true;
      // }, delay);
    } catch (error) {
      // Reset initialization flag on error
      isInitializedRef.current = false;
    }
  }, [
    userConfig,
    podcastId,
    podcastName,
    stationId,
    stationName,
    clipSlugAndId,
    token,
  ]);

  // Clean up player coordinator on component unmount
  useEffect(() => {
    return () => {
      playerCoordinator.cleanup();
    };
  }, []);

  // Convert numeric dimensions to pixel strings
  const componentStyle: Record<string, string | number> = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    position: 'relative',
    '--gencl-color-primary': '#c6002b',
    ...style,
  };

  try {
    // Render Clips SDK container with required data attributes
    return (
      <>
        <div
          className={`gen-sdk-class ${className}`}
          data-ads-enabled={isAdsEnabled ?? false}
          data-api-key={apiKey}
          data-placement-id={placementId}
          data-style-id={styleId}
          data-theme={theme}
          data-website-type={highlightsSDKSourceType}
          id="gen-sdk-2"
          style={componentStyle}
        >
          <SpinnerContainer isDark={theme.colors.gray.primary === '#28231f'}>
            <LoadingIcon size="28px" stroke="#c6002b" strokeWidth={3} />
          </SpinnerContainer>
        </div>

        {showExpandViewLoader && (
          <div
            className="highlights-loader"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'black',
              zIndex: 9999,
            }}
          >
            <LoadingIcon size="48px" stroke="#c6002b" strokeWidth={3} />
            <style>{`
              @media (min-width: 1025px) {
                .highlights-loader {
                  top: 0 !important;
                  left: 0 !important;
                  bottom: 0 !important;
                }
              }
            `}</style>
          </div>
        )}
      </>
    );
  } catch (error) {
    // Return a fallback div if there's a rendering error
    return (
      <div
        className={`gen-sdk-class ${className} error-fallback`}
        style={componentStyle}
      >
        {/* Clips content unavailable */}
      </div>
    );
  }
}

export default HighlightsSDK;
